import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const Attendance = () => {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [students, setStudents] = useState([]);
  const [attendanceData, setAttendanceData] = useState({});
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        // Fetch teacher profile first
        const profileResponse = await api.get(`/teachers/user/${user?._id}`);
        
        // Fetch classes assigned to this teacher
        const response = await api.get(`/classes/teacher/${profileResponse.data.data._id}`);
        setClasses(response.data.data);
      } catch (error) {
        console.error('Error fetching classes:', error);
        // Fallback to mock data if API fails
        setClasses([
          { id: 1, name: 'Class 10-A', subject: 'Mathematics' },
          { id: 2, name: 'Class 10-B', subject: 'Mathematics' },
          { id: 3, name: 'Class 9-A', subject: 'Mathematics' },
        ]);
      }
    };

    if (user) {
      fetchClasses();
    }
  }, [user]);

  useEffect(() => {
    if (selectedClass) {
      const fetchStudents = async () => {
        try {
          const response = await api.get(`/students/class/${selectedClass}`);
          const fetchedStudents = response.data.data;
          
          setStudents(fetchedStudents);

          // Initialize attendance data
          const initialAttendance = {};
          fetchedStudents.forEach(student => {
            initialAttendance[student._id] = 'present'; // Default to present
          });
          setAttendanceData(initialAttendance);
        } catch (error) {
          console.error('Error fetching students:', error);
          // Fallback to mock data if API fails
          const mockStudents = [
            { id: 1, userId: { name: 'John Smith', roll: '10A001' } },
            { id: 2, userId: { name: 'Emma Johnson', roll: '10A002' } },
            { id: 3, userId: { name: 'Michael Brown', roll: '10A003' } },
            { id: 4, userId: { name: 'Sarah Davis', roll: '10A004' } },
            { id: 5, userId: { name: 'Robert Wilson', roll: '10A005' } },
          ];

          setStudents(mockStudents);

          // Initialize attendance data
          const initialAttendance = {};
          mockStudents.forEach(student => {
            initialAttendance[student.id] = 'present'; // Default to present
          });
          setAttendanceData(initialAttendance);
        }
      };
      
      fetchStudents();
    }
  }, [selectedClass]);

  const handleAttendanceChange = (studentId, status) => {
    setAttendanceData(prev => ({
      ...prev,
      [studentId]: status
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Prepare attendance data for API submission
      const attendancePayload = Object.keys(attendanceData).map(studentId => ({
        studentId,
        status: attendanceData[studentId],
        date,
        classId: selectedClass
      }));

      const response = await api.post('/attendance/bulk', {
        attendanceData: attendancePayload,
        date,
        classId: selectedClass,
        teacherId: user._id
      });

      setSuccessMessage('Attendance marked successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      setErrorMessage('Failed to mark attendance. Please try again.');
      console.error('Error marking attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present':
        return 'bg-green-100 text-green-800';
      case 'absent':
        return 'bg-red-100 text-red-800';
      case 'late':
        return 'bg-yellow-100 text-yellow-800';
      case 'excused':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Attendance Management</h1>

        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Class
                </label>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  required
                >
                  <option value="">Choose a class</option>
                  {classes.map(cls => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name} ({cls.subject})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  required
                />
              </div>

              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={loading || !selectedClass}
                  className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Saving...' : 'Save Attendance'}
                </button>
              </div>
            </div>

            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded mb-4">
                {successMessage}
              </div>
            )}

            {errorMessage && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded mb-4">
                {errorMessage}
              </div>
            )}
          </form>
        </div>

        {selectedClass && students.length > 0 && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Students in {classes.find(c => c.id == selectedClass)?.name}
              </h2>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Roll No.
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {students.map((student) => (
                      <tr key={student.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {student.userId?.roll || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {student.userId?.name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(attendanceData[student.id])}`}>
                            {attendanceData[student.id]?.charAt(0).toUpperCase() + attendanceData[student.id]?.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex space-x-2">
                            {['present', 'absent', 'late', 'excused'].map(status => (
                              <button
                                key={status}
                                type="button"
                                onClick={() => handleAttendanceChange(student.id, status)}
                                className={`px-3 py-1 rounded text-xs font-medium ${
                                  attendanceData[student.id] === status
                                    ? 'bg-primary text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-800 mb-3">Attendance Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-green-100 rounded">
                    <div className="text-2xl font-bold text-green-800">
                      {Object.values(attendanceData).filter(status => status === 'present').length}
                    </div>
                    <div className="text-sm text-green-700">Present</div>
                  </div>
                  <div className="text-center p-3 bg-red-100 rounded">
                    <div className="text-2xl font-bold text-red-800">
                      {Object.values(attendanceData).filter(status => status === 'absent').length}
                    </div>
                    <div className="text-sm text-red-700">Absent</div>
                  </div>
                  <div className="text-center p-3 bg-yellow-100 rounded">
                    <div className="text-2xl font-bold text-yellow-800">
                      {Object.values(attendanceData).filter(status => status === 'late').length}
                    </div>
                    <div className="text-sm text-yellow-700">Late</div>
                  </div>
                  <div className="text-center p-3 bg-blue-100 rounded">
                    <div className="text-2xl font-bold text-blue-800">
                      {Object.values(attendanceData).filter(status => status === 'excused').length}
                    </div>
                    <div className="text-sm text-blue-700">Excused</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {!selectedClass && (
          <div className="text-center py-12">
            <p className="text-gray-600">Please select a class to view and mark attendance.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Attendance;